/**
 * 🔒 The typed checklist items (piece 29; T389, P29-11; decision 2110). A
 * checklist item on a job asks for a tick, a reading with its unit, pass or
 * fail, a photo or a signature. A tick is the item as the checklist family
 * already draws it and has no row of its own; the other four are typed on the
 * template item by a `ChecklistTemplateItemInput` row and copied at
 * instantiation onto a `ChecklistItemInput` row, where the answer is written.
 * 🔨 The item closes on its answer, never on a bare tick (T519's rule).
 */
import { fill, t, type StringKey } from '../strings';

/** The typed kinds, the database enum's members in its own order; Choice joined with decision 2112 (T1168). */
export const CHECKLIST_INPUT_KINDS = ['Reading', 'PassFail', 'Photo', 'Signature', 'Choice'] as const;
export type ChecklistInputKind = (typeof CHECKLIST_INPUT_KINDS)[number];

/** What an item asks for: a tick, or one of the typed kinds. */
export type ChecklistAnswerKind = 'Tick' | ChecklistInputKind;
export const CHECKLIST_ANSWER_KINDS: readonly ChecklistAnswerKind[] = ['Tick', ...CHECKLIST_INPUT_KINDS];

/** The `t()` key each kind is named by, in the template editor's picker and on the job. */
export const CHECKLIST_ANSWER_KIND_LABEL_KEYS: Readonly<Record<ChecklistAnswerKind, StringKey>> = {
  Tick: 'checklist.kind.tick',
  Reading: 'checklist.kind.reading',
  PassFail: 'checklist.kind.passFail',
  Photo: 'checklist.kind.photo',
  Signature: 'checklist.kind.signature',
  Choice: 'checklist.kind.choice',
};

export function isChecklistAnswerKind(value: unknown): value is ChecklistAnswerKind {
  return typeof value === 'string' && (CHECKLIST_ANSWER_KINDS as readonly string[]).includes(value);
}

/** What the template editor writes for one item: its kind and, on a Reading, the unit and the range. */
export interface TemplateItemInputDraft {
  inputKind: ChecklistAnswerKind;
  unitId?: string | null;
  lowLimit?: number | null;
  highLimit?: number | null;
  /** 🔒 Decision 2112: a Choice's options in the order they are offered. */
  options?: readonly string[] | null;
}

/** 🔒 Decision 2112: a Choice's options as typed, trimmed and blank lines dropped. */
export function choiceOptions(options: readonly string[] | null | undefined): string[] {
  return (options ?? []).map((option) => option.trim()).filter((option) => option.length > 0);
}

/** Why a template item's typing is refused, or null where it saves. */
export function templateInputProblem(draft: TemplateItemInputDraft): string | null {
  if (!isChecklistAnswerKind(draft.inputKind)) return 'an item asks for a tick, a reading, pass or fail, a photo, a signature or a choice';
  const low = draft.lowLimit ?? null;
  const high = draft.highLimit ?? null;
  const options = choiceOptions(draft.options);
  if (draft.inputKind === 'Choice') {
    if (options.length < 2) return 'a Choice offers two options or more';
    if (new Set(options.map((option) => option.toLowerCase())).size !== options.length) return 'a Choice names each option once';
  } else if (options.length > 0) return 'only a Choice names options';
  if (draft.inputKind !== 'Reading') {
    if ((draft.unitId ?? null) !== null || low !== null || high !== null) return 'only a Reading names a unit and a range';
    return null;
  }
  if (!draft.unitId) return 'a Reading names the unit it is asked in';
  if ((low !== null && !Number.isFinite(low)) || (high !== null && !Number.isFinite(high))) return 'a limit is a number';
  if (low !== null && high !== null && low > high) return 'the low limit is at or below the high limit';
  return null;
}

/** A unit's own conversion into its family's base, the shape `ComponentAttribute` converts by. */
export interface UnitConversion {
  conversionFactor: number;
  conversionOffset: number;
}

export function toBaseValue(value: number, unit: UnitConversion): number {
  return value * unit.conversionFactor + unit.conversionOffset;
}

export function fromBaseValue(base: number, unit: UnitConversion): number {
  return unit.conversionFactor === 0 ? base : (base - unit.conversionOffset) / unit.conversionFactor;
}

/** 🔒 A reading outside the copied limits is saved and flagged, never refused; the flag is derived and never stored. */
export function readingOutOfRange(baseValue: number | null, lowBaseLimit: number | null, highBaseLimit: number | null): boolean {
  if (baseValue === null) return false;
  if (lowBaseLimit !== null && baseValue < lowBaseLimit) return true;
  return highBaseLimit !== null && baseValue > highBaseLimit;
}

/** What a client sends to answer one item; a Photo arrives as an upload on its own route. */
export type ChecklistAnswerInput =
  | { kind: 'Tick' }
  | { kind: 'Reading'; value: string; unitId?: string | null }
  | { kind: 'PassFail'; passed: boolean; note?: string | null }
  | { kind: 'Signature'; signerName: string; image: string }
  | { kind: 'Choice'; choice: string };

/** Why an answer is refused before anything is written, or null where it saves. */
export function checklistAnswerProblem(asked: ChecklistAnswerKind, input: ChecklistAnswerInput | null | undefined): string | null {
  if (!input || typeof input !== 'object') return 'the answer names what the item asks for';
  if (asked === 'Photo') return 'a Photo item is answered by taking or uploading the photo';
  if (input.kind !== asked) return `this item asks for ${asked === 'Tick' ? 'a tick' : asked === 'PassFail' ? 'Pass or Fail' : asked === 'Choice' ? 'one of its options' : `a ${asked}`}`;
  if (input.kind === 'Reading') {
    const text = typeof input.value === 'string' ? input.value.trim() : '';
    if (text === '' || !Number.isFinite(Number(text))) return 'a reading is a number';
  }
  if (input.kind === 'PassFail') {
    if (typeof input.passed !== 'boolean') return 'the answer is Pass or Fail';
    if (!input.passed && !(input.note ?? '').trim()) return 'a Fail asks for a note saying what failed';
  }
  if (input.kind === 'Signature' && !(input.signerName ?? '').trim()) return 'a signature carries the signer’s typed name';
  if (input.kind === 'Choice' && !(input.choice ?? '').trim()) return 'a Choice is answered by picking one option';
  return null;
}

/** One answer as the job reads it back. */
export interface ChecklistAnswerView {
  enteredValue: string | null;
  unitAbbreviation: string | null;
  passed: boolean | null;
  note: string | null;
  fileId: string | null;
  signerName: string | null;
  answeredAt: string | null;
  answeredBy: string | null;
}

/** A unit a Reading item is offered in: its own family's active units. */
export interface ChecklistUnitOption {
  id: string;
  label: string;
}

/** 🔒 One item on the job's checklists, as the visit's page and My Jobs draw it. */
/**
 * 🔒 Decision 2280: the procedure a checklist item cites, as the template
 * editor and the job's checklist read it: the article's newest published
 * title and version, and whether the article was archived after it was cited.
 */
export interface ChecklistProcedure {
  articleId: string;
  title: string;
  /** The newest published version's number; null on an article never published, which is never offered. */
  versionNumber: number | null;
  archived: boolean;
}

/** 🔒 Decision 2280: a procedure as a line reads it, its title and newest version, marked Archived where it was archived after citation. */
export function procedureText(procedure: ChecklistProcedure): string {
  return [procedure.title, procedureDetailText(procedure)].filter(Boolean).join(' · ');
}

/** The version and the archived note of a cited procedure without its title, which the Open Procedure window already carries as its own (T1700). */
export function procedureDetailText(procedure: ChecklistProcedure): string {
  const version = procedure.versionNumber === null ? null : `${t('kb.version')} ${procedure.versionNumber}`;
  return [version, procedure.archived ? t('checklist.procedure.archived') : null].filter(Boolean).join(' · ');
}

/** 🔒 Decision 2280: one article the template editor's Procedure field offers, published and never archived. */
export interface ChecklistProcedureOption {
  id: string;
  title: string;
  versionNumber: number;
}

export interface JobChecklistItem {
  id: string;
  /** 🔒 Decision 12580: the checklist the item stands on, which names its work instruction; absent on a read from before it. */
  checklistId?: string;
  checklistName: string;
  title: string;
  description: string | null;
  status: 'Open' | 'Done' | 'Dismissed';
  kind: ChecklistAnswerKind;
  /** The unit the item is asked or was answered in, and the units of its family. */
  unitId: string | null;
  units: readonly ChecklistUnitOption[];
  /** The range in the item's own unit, for the hint beside the field. */
  lowLimit: number | null;
  highLimit: number | null;
  /** 🔒 Decision 2112: a Choice's options, copied onto the job; empty on every other kind. */
  options: readonly string[];
  answer: ChecklistAnswerView | null;
  outOfRange: boolean;
  /** 🔒 Decision 2111: who signed the item off unanswered, and why; null on an item answered or still open. */
  signedOff: { by: string | null; reason: string; at: string | null } | null;
  /**
   * 🔒 Decision 2280: the procedure the item cites, its newest published version; null where it cites none or
   * where the viewer's read guard refuses the article, which then is never named. Absent on a read from before it.
   */
  procedure?: ChecklistProcedure | null;
  /** 🔒 Decision 2400: the version number a completed item was completed against; null on an open or uncited item. */
  completedAgainstVersion?: number | null;
}

/** The job's checklist read: every item on the checklists tagged to the Work Order, and what this viewer may press. */
export interface JobChecklistAnswer {
  workOrderId: string;
  workOrderNumber: string;
  items: readonly JobChecklistItem[];
  canAnswer: boolean;
  /** 🔒 Decision 2111: whether this viewer signs off an unanswered item, a manager above a worker on the job or a role that edits every Work Order. */
  canSignOff: boolean;
  /**
   * 🔒 Decision 12580: each checklist on the job naming a work instruction the viewer's article read guard admits, in
   * the order the checklists were written; absent on a read from before it.
   */
  workInstructions?: readonly JobWorkInstruction[];
}

/** 🔒 Decision 12581: one photo taken against an item in the working view, a File tagged to the Work Order. */
export interface ChecklistItemPhotoRow {
  fileId: string;
  filename: string;
  addedAt: string;
}

/**
 * 🔒 Decision 12581: one item as the working view draws it: the job's item with the step it was made from, the note
 * typed against it and its photos. `articleId` is named only where it is the work instruction's own article.
 */
export interface ChecklistWorkingItem extends JobChecklistItem {
  articleId: string | null;
  articleStepKey: string | null;
  workNote: string | null;
  photos: readonly ChecklistItemPhotoRow[];
}

/** 🔒 Decision 12581: the working view's read of one job's checklist and the article it follows. */
export interface ChecklistWorkingView {
  workOrderId: string;
  workOrderNumber: string;
  checklistId: string;
  checklistName: string;
  /** The work instruction where the viewer's article read guard admits it; null where it names none or the guard refuses. */
  workInstruction: ChecklistProcedure | null;
  items: readonly ChecklistWorkingItem[];
  canAnswer: boolean;
}

/** 🔒 Decision 12581: the working view's read of one job's checklist, the one path the web and the phone both call. */
export function checklistWorkingPath(workOrderId: string, checklistId: string): string {
  return `/work-orders/${encodeURIComponent(workOrderId)}/checklists/${encodeURIComponent(checklistId)}/working`;
}

/** 🔒 Decision 12581: the longest note a working view keeps against an item. */
export const CHECKLIST_WORK_NOTE_MAX = 2000;

/** Why a note is refused, or null; an empty note clears the one standing. */
export function workNoteProblem(note: unknown): string | null {
  if (note !== null && typeof note !== 'string') return 'a note is words';
  return typeof note === 'string' && note.trim().length > CHECKLIST_WORK_NOTE_MAX ? `a note is at most ${CHECKLIST_WORK_NOTE_MAX} characters` : null;
}

/** 🔒 Decision 12580: one checklist on a job and the article it follows, which Open Work Instruction opens. */
export interface JobWorkInstruction {
  checklistId: string;
  checklistName: string;
  procedure: ChecklistProcedure;
}

/** The answered count a card shows, an item no longer Open counting as closed. */
export function checklistProgress(items: readonly Pick<JobChecklistItem, 'status'>[]): { closed: number; total: number } {
  return { closed: items.filter((item) => item.status !== 'Open').length, total: items.length };
}


/** 🔒 An answer in the words the job and the completion sheet read it in: the reading and its unit, Pass or Fail with its note, the photo, the signer. */
export function checklistAnswerText(item: Pick<JobChecklistItem, 'kind' | 'answer' | 'outOfRange'>): string {
  const a = item.answer;
  if (!a) return '';
  if (item.kind === 'Reading') {
    const reading = [a.enteredValue, a.unitAbbreviation].filter(Boolean).join(' ');
    return item.outOfRange ? `${reading} (${t('checklist.job.outOfRange')})` : reading;
  }
  if (item.kind === 'PassFail') return a.passed ? t('checklist.job.passed') : fill(t('checklist.job.failed'), { note: a.note ?? '' });
  if (item.kind === 'Signature') return fill(t('checklist.job.signedBy'), { name: a.signerName ?? '' });
  if (item.kind === 'Photo') return t('checklist.job.photoTaken');
  // 🔒 Decision 2112: a Choice reads as its pick, the way a Pass reads.
  if (item.kind === 'Choice') return a.enteredValue ?? '';
  return t('checklist.job.done');
}

/**
 * 🔒 Decision 2111 (T889): why the job, or a visit completed in full, does not
 * complete while items stand unanswered, naming each; null where none stands.
 */
export function checklistStopProblem(unanswered: readonly string[]): string | null {
  if (unanswered.length === 0) return null;
  const count = unanswered.length === 1 ? '1 checklist item is' : `${unanswered.length} checklist items are`;
  return `${count} unanswered: ${unanswered.join(', ')}. Answer each, or a manager signs off on why it was not done`;
}

/** 🔒 Decision 2111: a sign-off says why the item was not done. */
export function signOffProblem(reason: string | null | undefined): string | null {
  return reason && reason.trim().length > 0 ? null : 'a sign-off says why the item was not done';
}
